import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Loader2, GitMerge, ShieldCheck, Mail, User, Zap, Phone, MapPin, UserSearch, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { MergeDialog } from './BulkActionBar';

const MATCH_META = {
  email:        { icon: Mail,       label: 'email',                    color: 'text-blue-600' },
  name:         { icon: User,       label: 'exact name',               color: 'text-purple-600' },
  phone:        { icon: Phone,      label: 'phone number',             color: 'text-emerald-600' },
  similar_name: { icon: UserSearch, label: 'similar name (nickname/typo)', color: 'text-amber-600' },
  address:      { icon: MapPin,     label: 'last name + address',      color: 'text-rose-600' },
};

/**
 * "Duplicates" tab — server groups donors by matching email or normalized name.
 * Each group has a "Review & merge" button that opens the MergeDialog pre-populated
 * with just that group.
 */
export function DuplicatesTab({ api, onMerged }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mergeGroup, setMergeGroup] = useState(null);
  const [confirmAll, setConfirmAll] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/donor-duplicates');
      setGroups(data);
    } catch { toast.error('Failed to load duplicates'); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const mergeAll = async () => {
    if (!confirmAll) { setConfirmAll(true); setTimeout(() => setConfirmAll(false), 6000); return; }
    setConfirmAll(false); setBusy(true);
    try {
      const { data } = await api.post('/donor-merge-auto', {});
      toast.success(`Merged ${data.groups_merged} group${data.groups_merged === 1 ? '' : 's'} (${data.records_merged} duplicate record${data.records_merged === 1 ? '' : 's'} folded in)`);
      load(); onMerged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Auto-merge failed'); }
    setBusy(false);
  };

  const quickMerge = async (g) => {
    setBusy(true);
    try {
      const { data } = await api.post('/donor-merge-auto', { match_type: g.match_type, match_value: g.match_value });
      toast.success(`Merged ${data.records_merged + data.groups_merged} records into one`);
      load(); onMerged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Quick merge failed'); }
    setBusy(false);
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-[#6B3FA0]" /></div>;

  return (
    <div className="space-y-3" data-testid="duplicates-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-gray-600">
          {groups.length === 0
            ? <>No duplicate donors detected — the list is clean. <ShieldCheck className="h-4 w-4 text-green-600 inline" /></>
            : <>{groups.length} suspected duplicate group{groups.length === 1 ? '' : 's'} found. Review and merge to consolidate history.</>}
        </p>
        <div className="flex gap-2">
          {groups.length > 0 && (
            <Button size="sm" onClick={mergeAll} disabled={busy}
                    className={confirmAll ? 'bg-red-600 hover:bg-red-700 text-white' : ''}
                    data-testid="dup-merge-all">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4 mr-1" />}
              {confirmAll ? `Confirm — merge all ${groups.length} groups` : 'Merge all groups'}
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={load} data-testid="dup-refresh">Refresh</Button>
        </div>
      </div>
      {groups.length > 0 && (
        <p className="text-xs text-gray-500">
          Auto-merge keeps the record with the most gifts (oldest wins ties), reassigns all gifts to it, unions tags, and fills any empty fields from the duplicates.
          “Merge all groups” only merges <strong>exact</strong> email/name matches — phone, similar-name, and address groups need a per-group review (family-linked and household members are never flagged).
        </p>
      )}

      {groups.map((g, i) => (
        <Card key={i} data-testid={`dup-group-${i}`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                {(() => { const m = MATCH_META[g.match_type] || MATCH_META.name; const I = m.icon;
                  return <><I className={`h-4 w-4 ${m.color}`} />
                    <span className="text-sm text-gray-500">Matched on {m.label}:</span></>; })()}
                <span className="font-semibold">{g.match_value}</span>
                <Badge variant="outline">{g.donors.length} records</Badge>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => quickMerge(g)} disabled={busy}
                        data-testid={`dup-quick-merge-${i}`}>
                  <Zap className="h-4 w-4 mr-1" /> Quick merge
                </Button>
                <Button size="sm" onClick={() => setMergeGroup(g)} data-testid={`dup-merge-${i}`}>
                  <GitMerge className="h-4 w-4 mr-1" /> Review & merge
                </Button>
              </div>
            </div>
            <div className="border rounded overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs text-gray-600 uppercase">
                  <tr>
                    <th className="text-left p-2">Name</th>
                    <th className="text-left p-2">Org</th>
                    <th className="text-left p-2">Email</th>
                    <th className="text-left p-2">Phone</th>
                    <th className="text-right p-2">Gifts</th>
                    <th className="text-right p-2">Total</th>
                    <th className="text-right p-2">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {g.donors.map(d => (
                    <tr key={d.id} className="border-t">
                      <td className="p-2 font-medium">{d.name}{d.needs_review && <Badge className="ml-1 bg-orange-100 text-orange-800 border-0 text-[10px]">review</Badge>}</td>
                      <td className="p-2 text-gray-600">{d.org || '—'}</td>
                      <td className="p-2 text-gray-600">{d.email || '—'}</td>
                      <td className="p-2 text-gray-600">{d.phone || '—'}</td>
                      <td className="p-2 text-right">{d.gift_count || 0}</td>
                      <td className="p-2 text-right font-medium">${(d.total_given || 0).toLocaleString()}</td>
                      <td className="p-2 text-right text-xs text-gray-500">{(d.created_at || '').slice(0, 10)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ))}

      <JunkSweepSection api={api} onSwept={() => { load(); onMerged(); }} />

      <MergeDialog open={!!mergeGroup} onClose={() => setMergeGroup(null)}
                   api={api} donors={mergeGroup?.donors || []}
                   onMerged={() => { load(); onMerged(); }} />
    </div>
  );
}

const JUNK_LABELS = {
  to_fill: { label: 'TO FILL stub', cls: 'bg-orange-100 text-orange-800' },
  empty:   { label: 'No name & no email', cls: 'bg-red-100 text-red-800' },
};

function JunkSweepSection({ api, onSwept }) {
  const [junk, setJunk] = useState([]);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get('/donor-junk'); setJunk(data); } catch { /* silent */ }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  if (junk.length === 0) return null;

  const sweep = async () => {
    if (!confirm) { setConfirm(true); setTimeout(() => setConfirm(false), 6000); return; }
    setConfirm(false); setBusy(true);
    try {
      const { data } = await api.post('/donor-junk-sweep', {});
      toast.success(`Swept ${data.deleted} junk record${data.deleted === 1 ? '' : 's'}`);
      load(); onSwept();
    } catch (e) { toast.error(e.response?.data?.detail || 'Sweep failed'); }
    setBusy(false);
  };

  const removeOne = async (id) => {
    try {
      await api.post('/donor-junk-sweep', { donor_ids: [id] });
      toast.success('Removed');
      load(); onSwept();
    } catch { toast.error('Failed'); }
  };

  return (
    <Card className="border-amber-200" data-testid="junk-sweep-card">
      <CardContent className="p-4">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
          <div className="flex items-center gap-2">
            <Trash2 className="h-4 w-4 text-amber-600" />
            <span className="font-semibold text-sm">Junk sweep</span>
            <Badge variant="outline">{junk.length} stub{junk.length === 1 ? '' : 's'}</Badge>
          </div>
          <Button size="sm" onClick={sweep} disabled={busy}
                  className={confirm ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-amber-600 hover:bg-amber-700 text-white'}
                  data-testid="junk-sweep-all-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4 mr-1" />}
            {confirm ? `Confirm — delete all ${junk.length}` : 'Sweep all junk'}
          </Button>
        </div>
        <p className="text-xs text-gray-500 mb-2">
          Zero-gift stub records: “TO FILL” placeholders and rows with no name and no email. Safe to delete — they hold no giving history.
        </p>
        <div className="border rounded overflow-hidden">
          <table className="w-full text-sm">
            <tbody>
              {junk.slice(0, 50).map(d => (
                <tr key={d.id} className="border-t first:border-t-0" data-testid={`junk-row-${d.id}`}>
                  <td className="p-2 font-medium">{d.name || <span className="text-gray-400 italic">no name</span>}</td>
                  <td className="p-2 text-gray-600">{d.email || '—'}</td>
                  <td className="p-2">
                    <Badge className={`${(JUNK_LABELS[d.junk_reason] || {}).cls || ''} border-0 text-[10px]`}>
                      {(JUNK_LABELS[d.junk_reason] || {}).label || d.junk_reason}
                    </Badge>
                  </td>
                  <td className="p-2 text-right text-xs text-gray-500">{(d.created_at || '').slice(0, 10)}</td>
                  <td className="p-2 text-right">
                    <Button size="sm" variant="ghost" onClick={() => removeOne(d.id)} data-testid={`junk-delete-${d.id}`}>
                      <Trash2 className="h-3.5 w-3.5 text-red-500" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {junk.length > 50 && <p className="text-xs text-gray-400 mt-1">Showing first 50 of {junk.length}.</p>}
      </CardContent>
    </Card>
  );
}
